/**
 * procedenciaVia — P1-S4 (§B cotejo P1RD1)
 *
 * Deriva la vía de resolución de coordenadas por fila a partir de los
 * campos que el pipeline YA emite (ProcessedInfrastructure.source +
 * normalization.corrections/yCorrectionType). No toca el pipeline:
 * es una lectura declarativa (DOP-P1S4-2=α, verificado 04-jul-2026).
 *
 * Las 5 vías del cotejo §B:
 *  - directa:       coords del documento válidas sin transformación
 *  - crs:           coords del documento reproyectadas (WGS84/ED50 → UTM30)
 *  - corregida:     coords del documento con corrección de valor
 *                   (swap X↔Y, Y truncada, miles europeos…) o sustituidas
 *                   por la referencia oficial (source corrected:*)
 *  - geocodificada: sin coords en documento, resueltas por geocodificador
 *  - centroide:     sin coords en documento ni en fuentes; el pipeline
 *                   asigna el centroide municipal (UINCEN 12-sep-2026 —
 *                   antes se confundía con «geocodificada»)
 *  - sin_geo:       sin coordenadas finales por ninguna vía
 *
 * @version 1.1.0
 */

import type {
  ExtractedInfrastructure,
  ProcessedInfrastructure,
} from '../types/processing';

// ─── Tipos ───────────────────────────────────────────────────────────────────

export type ProcedenciaVia =
  | 'directa'
  | 'crs'
  | 'corregida'
  | 'geocodificada'
  | 'centroide'
  | 'sin_geo';

/** Etiquetas visibles (RAE) por vía */
export const VIA_LABELS: Record<ProcedenciaVia, string> = {
  directa: 'Directa',
  crs: 'CRS',
  corregida: 'Corregida',
  geocodificada: 'Geocod.',
  centroide: 'Centroide',
  sin_geo: 'Sin geo',
};

/** Etiquetas largas (tooltips / desglose) */
export const VIA_LABELS_LARGO: Record<ProcedenciaVia, string> = {
  directa: 'Directa del documento',
  crs: 'Convertida de CRS',
  corregida: 'Corregida',
  geocodificada: 'Geocodificada',
  centroide: 'Centroide municipal (no es la ubicación del elemento)',
  sin_geo: 'Sin coordenadas',
};

export const VIAS_ORDEN: readonly ProcedenciaVia[] = [
  'directa',
  'crs',
  'corregida',
  'geocodificada',
  'centroide',
  'sin_geo',
] as const;

/**
 * UINCEN — ¿el `source` del pipeline significa «centroide municipal»?
 * Familia reconocida (un solo sitio; `useAutoGeocoding` sella el sufijo):
 *  - `geocoded:<geocoderUsed>:centroide` (CENT60, caso B)
 *  - `iner2:centroide-municipio` (Fix-B / universal / social sin sufijo)
 *  - `centroid` (Issue #14-B: sin fuentes + centroide INER2, confianza 20)
 */
export function esOrigenCentroide(source: string | undefined): boolean {
  if (!source) return false;
  return source === 'centroid'
    || source === 'iner2:centroide-municipio'
    || source.endsWith(':centroide');
}

/**
 * CENT60 (12-sep-2026): productores cuyo resultado es el centroide del municipio,
 * no una ubicación (`DOP-CENT60-FAMILIA`=(b): solo los municipales; los
 * `pre_centroide` de Catastro/CDAU son candidatos pre-refinamiento y quedan fuera).
 * `universal`, `social` y `Fix-B` comparten `source: 'iner2:centroide-municipio'`;
 * el de telecom se reconoce por `geocoderUsed`.
 *
 * CTRN-G (13-sep-2026): mudada desde `useAutoGeocoding` sin cambiar el cuerpo
 * (`DOP-CTRNG-1`=(a)) para que el orquestador la consulte sin importar del hook.
 */
const CENTROIDE_MUNICIPAL_GEOCODERS: ReadonlySet<string> = new Set([
  'specialized:universal:centroide',
  'specialized:social:centroide',
  'iner2:centroide-municipio',
  'specialized:telecom:centroid_fallback',
]);

export function esCentroideMunicipal(
  geocoderUsed: string | undefined,
  source: string | undefined,
): boolean {
  return source === 'iner2:centroide-municipio'
    // UINCEN: Issue #14-B (sin fuentes + centroide INER2) conserva el geocoderUsed
    // previo y solo marca `source: 'centroid'` (OBS-GEO-ISSUE14B-CENTROID-FUERA-DE-FAMILIA-CENT60)
    || source === 'centroid'
    || (geocoderUsed !== undefined && CENTROIDE_MUNICIPAL_GEOCODERS.has(geocoderUsed));
}

// ─── Conjuntos de clasificación ──────────────────────────────────────────────

/** yCorrectionType que implican reproyección de CRS (vía «crs») */
const CRS_CORRECTIONS: ReadonlySet<string> = new Set([
  'WGS84_TO_UTM30',
  'WGS84_INV_TO_UTM30',
  'ED50_TO_ETRS89',
  'WGS84_CENTROID_CONVERTED',
]);

/** yCorrectionType que alteran el VALOR sin cambiar de CRS (vía «corregida») */
const VALUE_Y_CORRECTIONS: ReadonlySet<string> = new Set([
  'INSERT_4',
  'MULTIPLY_10',
  'EXTRA_DIGIT_REMOVAL',
  'UTM30_MILES_EUROPEOS',
]);

/**
 * CorrectionType de normalización que alteran el valor numérico.
 * WHITESPACE_CLEANED y ENCODING_FIXED son limpieza cosmética → siguen
 * contando como vía «directa».
 */
const VALUE_CORRECTIONS: ReadonlySet<string> = new Set([
  'Y_TRUNCATED',
  'XY_SWAPPED',
  'PLACEHOLDER_DETECTED',
  'SEPARATOR_FIXED',
  'THOUSANDS_REMOVED',
  'DECIMAL_FIXED',
]);

/** source del pipeline que significan «sin coordenadas finales» */
const SIN_GEO_SOURCES: ReadonlySet<string> = new Set([
  'none',
  'pending',
  'geocoding_error',
  'geocoding_failed',
]);

function esProcesada(
  infra: ExtractedInfrastructure | ProcessedInfrastructure
): infra is ProcessedInfrastructure {
  return 'score' in infra && 'source' in infra;
}

// ─── Derivación por fila ─────────────────────────────────────────────────────

/**
 * Deriva la vía de resolución de una fila.
 *
 * @param infra     Infraestructura (cruda o procesada por el pipeline)
 * @param hasCoords ¿Tiene coordenadas finales válidas? (lo calcula el
 *                  adaptador con las mismas x/y que pinta la tabla)
 */
export function deriveProcedencia(
  infra: ExtractedInfrastructure | ProcessedInfrastructure,
  hasCoords: boolean
): ProcedenciaVia {
  if (!hasCoords) return 'sin_geo';

  // Sin pipeline (ExtractedInfrastructure cruda): coords del documento
  // tal cual → provisionalmente «directa».
  if (!esProcesada(infra)) return 'directa';

  const source = infra.source ?? '';
  if (SIN_GEO_SOURCES.has(source)) return 'sin_geo';
  // UINCEN: antes que «geocodificada» — el centroide llega con prefijo geocoded:
  if (esOrigenCentroide(source)) return 'centroide';
  if (source.startsWith('geocoded:')) return 'geocodificada';
  if (source.startsWith('corrected:')) return 'corregida';

  // Coords del documento conservadas (confirmed:document,
  // original:minor_discrepancy, conflict:manual_review, normalized…):
  // distinguir directa / crs / corregida por la normalización aplicada.
  const norm = infra.normalization;
  const yType = norm?.yCorrectionType ?? null;
  if (yType && CRS_CORRECTIONS.has(yType)) return 'crs';
  if (yType && VALUE_Y_CORRECTIONS.has(yType)) return 'corregida';
  const tieneCorreccionValor = (norm?.corrections ?? []).some(c =>
    VALUE_CORRECTIONS.has(c.type)
  );
  if (tieneCorreccionValor) return 'corregida';

  return 'directa';
}

// ─── Motivo del residuo sin geo ──────────────────────────────────────────────

/**
 * Deriva el motivo por el que una fila quedó sin geolocalizar.
 * Solo tiene sentido cuando deriveProcedencia() === 'sin_geo'.
 * Derivación defensiva: reviewReason del orquestador si existe;
 * si no, diagnóstico por los datos del documento.
 */
export function deriveMotivoSinVia(
  infra: ExtractedInfrastructure | ProcessedInfrastructure
): string {
  if (esProcesada(infra)) {
    const reason =
      infra.geocoding?.reviewReason ??
      infra.crossValidation?.reviewReason;
    if (reason) return reason;
    if (infra.source === 'geocoding_error') {
      return 'Error durante la geocodificación';
    }
  }
  const sinDireccion = !infra.direccion?.trim();
  if (sinDireccion) {
    return 'Sin dirección en el documento; la geocodificación por dirección no es posible';
  }
  return 'Sin coordenadas en el documento ni en las fuentes de referencia';
}

// ─── Aviso del centroide forzado ─────────────────────────────────────────────

/**
 * CENFOR-A (13-sep-2026) — ¿trajo el documento una coordenada que el parser
 * rechazó? Señal medida en Alfacar («Trincheras…», Y de 8 dígitos):
 * `isValidUTMValue` deja `hasCoordinates=false` pero conserva `xOriginal`/
 * `yOriginal` crudos y `normalizeInfrastructure` nunca calcula `normalization`.
 * Ojo (V corta CENFOR-A): tras el geocodificador `applyGeocodingResult` pone
 * `hasCoordinates=true` (significa «tiene coordenada final»), así que en una
 * fila procesada la señal es la AUSENCIA de `normalization` (o `isValid=false`),
 * no el flag. Un par `0 / 0` es un marcador de «sin coordenada», no inválida.
 * Solo tiene sentido sobre filas cuya procedencia es `centroide`.
 */
export function coordenadaDocumentoInvalida(
  infra: ExtractedInfrastructure | ProcessedInfrastructure
): boolean {
  const x = parseFloat(String(infra.xOriginal ?? ''));
  const y = parseFloat(String(infra.yOriginal ?? ''));
  if (isNaN(x) || isNaN(y) || (x === 0 && y === 0)) return false;
  if (!infra.hasCoordinates) return true;
  if (esProcesada(infra)) {
    const norm = infra.normalization;
    if (!norm) return true;
    if (norm.isValid === false) return true;
  }
  return false;
}

/**
 * CENFOR-A — texto que viaja con el elemento forzado al centroide municipal
 * (`MOTIVO` / `OBSERVACIONES` del entregable; reutilizable por la UI en
 * CENFOR-B). Solo tiene sentido cuando deriveProcedencia() === 'centroide'.
 * Distingue los dos casos que Luis pidió separar (cierre de CTRN-G):
 *  - «no encontrado»: ninguna fuente localizó el elemento (Colomera 54/61).
 *  - «coordenada del documento inválida»: el ODT traía X/Y y se descartó
 *    (OBS-P1-COORD-INVALIDA-DEL-DOC-CAE-A-CENTROIDE-SIN-AVISO).
 * Redacción sellada por Luis (DOP-CENFORA-3, 13-sep-2026).
 */
export function avisoCentroide(
  infra: ExtractedInfrastructure | ProcessedInfrastructure
): string {
  if (coordenadaDocumentoInvalida(infra)) {
    const x = formatearOriginal(infra.xOriginal);
    const y = formatearOriginal(infra.yOriginal);
    return `Ubicación forzada al centroide municipal: la coordenada del documento (X ${x} / Y ${y}) no es válida. Corregir en el documento.`;
  }
  return 'Ubicación forzada al centroide municipal: ninguna fuente localizó el elemento. Revisar y corregir la coordenada.';
}

/** Coma decimal (RAE) para el valor crudo del documento, sin tocar los dígitos. */
function formatearOriginal(valor: string | undefined): string {
  return String(valor ?? '').trim().replace('.', ',');
}

// ─── Desglose agregado ───────────────────────────────────────────────────────

export type ViaBreakdown = Record<ProcedenciaVia, number>;

export function emptyViaBreakdown(): ViaBreakdown {
  return { directa: 0, crs: 0, corregida: 0, geocodificada: 0, centroide: 0, sin_geo: 0 };
}
